import React, { useState, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-hot-toast";
import {
  addQrPageToPdf,
  createQrPdfDocument,
  QR_CANVAS_PX,
  QR_PDF_JPEG_QUALITY,
  waitForQrCanvasRender,
} from "../../utils/qrPdfConfig";

const QrGenerator: React.FC = () => {
  const [sku, setSku] = useState("");
  const [cantidad, setCantidad] = useState(1);
  const [qrData, setQrData] = useState<{ sku: string; lote: string; numero_de_serie: string; id_qr: string }[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const qrCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  const generateQrCodes = async () => {
    try {
      setIsGenerating(true);
      const loadingToast = toast.loading('Generando códigos QR...');
      
      const response = await axiosInstance.post("/qr/generate", { sku, cantidad });
      
      console.log("Respuesta completa de la API:", response.data);
  
      if (response.data && Array.isArray(response.data.qrData)) {
        setQrData(response.data.qrData); 
        toast.success('Códigos QR generados exitosamente', { id: loadingToast });
      } else {
        console.error("Error: La respuesta de la API no contiene 'qrList'.", response.data);
        throw new Error("La respuesta de la API no contiene 'qrList'.");
      }
    } catch (error) {
      console.error("Error generando QR:", error);
      toast.error("Hubo un error generando los QR.");
    } finally {
      setIsGenerating(false);
    }
  };
  

  const generatePdf = async () => {
    if (!qrData || qrData.length === 0) {
      toast.error("No hay códigos QR generados.");
      return;
    }

    try {
      setIsGenerating(true);
      const loadingToast = toast.loading('Generando PDF...', {
        duration: Infinity // Mantener el toast hasta que se complete
      });
  
      await waitForQrCanvasRender();

      const doc = createQrPdfDocument();

      for (let i = 0; i < qrData.length; i++) {
        const qrCanvas = qrCanvasRefs.current[i];
        if (!qrCanvas) continue;

        const qrImgData = qrCanvas.toDataURL("image/jpeg", QR_PDF_JPEG_QUALITY);
        addQrPageToPdf(doc, qrImgData, qrData[i].id_qr, i === 0);

        qrCanvasRefs.current[i] = null;
      }

      // Limpiar el estado después de generar el PDF
      setQrData([]);
      qrCanvasRefs.current = [];

      doc.save(`QRs_${sku}.pdf`);
      toast.success('PDF generado exitosamente', { id: loadingToast });
    } catch (error) {
      console.error("Error generando el PDF:", error);
      toast.error("Hubo un error generando el PDF.");
    } finally {
      setIsGenerating(false);
    }
  };
  

   return (
    <div className="flex flex-col items-center gap-6 p-6 rounded-lg shadow-md w-full max-w-md mx-auto bg-white">
      <h2 className="text-xl font-bold text-custom-blue-2">Generador de Códigos QR</h2>
      <input
          type="text"
          placeholder="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value.toUpperCase())}
          className="w-full p-2 border border-custom-gray-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-custom-blue"
        />      

      <input
        type="number"
        placeholder="Cantidad"
        value={cantidad}
        onChange={(e) => setCantidad(Number(e.target.value))}
        className="w-full p-2 border border-custom-gray-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-custom-blue"
      />
      <button
        onClick={generateQrCodes}
        disabled={isGenerating}
        className={`w-full p-2 hover:bg-bigcenter text-white rounded-lg bg-custom-blue transition ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isGenerating ? 'Generando...' : 'Generar QR'}
      </button>
      <button
        onClick={generatePdf}
        disabled={!qrData.length || isGenerating}
        className={`w-full p-2 rounded-lg transition ${qrData.length && !isGenerating ? 'bg-confirmar hover:bg-hexa text-white' : 'bg-gray-400 text-gray-700 cursor-not-allowed'}`}
      >
        Descargar PDF
      </button>
      <div className="hidden">
        {qrData.map((qr, index) => (
          <QRCodeCanvas
            key={index}
            value={`${qr.id_qr}`}
            size={QR_CANVAS_PX}
            level="H"
            ref={(el) => (qrCanvasRefs.current[index] = el)}
          />
        ))}
      </div>
    </div>
  );
};

export default QrGenerator;
